<app-page-title title="Advanced Search" />

<app-spinner [visible]="loading" label="Searching..." [blockUI]="false" />

<app-section-card [contentPadding]="24">
  <!-- Search Box Area -->
  <div class="flex flex-column align-items-center gap-3 mb-7">
    <div class="flex align-items-center gap-3" style="width: 100%; max-width: 700px">
      <p-iconfield class="flex-1">
        <p-inputicon styleClass="pi pi-search" />
        <input pInputText [(ngModel)]="searchText" class="w-full" placeholder="Search..." (keyup.enter)="onSearch()" />
      </p-iconfield>
      <p-button label="Search" icon="pi pi-search" (onClick)="onSearch()"></p-button>
    </div>
  </div>

  <!-- Tabs Area -->
  <p-tabs [(value)]="activeTab" (valueChange)="onSearch()">
    <p-tablist>
      <p-tab *ngFor="let tab of tabs" [value]="tab.value">{{ tab.label }}</p-tab>
    </p-tablist>
    <p-tabpanels>
      <p-tabpanel *ngFor="let tab of tabs" [value]="tab.value">
        <div class="search-results">
          <!-- Companies -->
          <div *ngIf="searchResult?.companies?.length && (tab.value === -1 || tab.value === EntityType.Company)" class="result-section">
            <div *ngFor="let company of searchResult!.companies" class="result-item">
              <div class="flex align-content-start gap-3">
                <i class="pi pi-building"></i>
                <div class="flex flex-column gap-2">
                  <h3>
                    <a [routerLink]="['/company', company.businessCode]" [innerHTML]="company.legalName | highlight : searchedKeyword" [attr.aria-label]="company.legalName">{{
                      company.legalName
                    }}</a>
                  </h3>
                  <div [innerHTML]="joinValues(company.businessCode, company.signer, company.emailAddress) | highlight : searchedKeyword"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Foreign Nationals -->
          <div *ngIf="searchResult?.foreignNationals?.length && (tab.value === -1 || tab.value === EntityType.ForeignNational)" class="result-section">
            <div *ngFor="let fn of searchResult!.foreignNationals" class="result-item">
              <div class="flex align-content-start gap-3">
                <i class="pi pi-user"></i>
                <div class="flex flex-column gap-2">
                  <h3>
                    <a [routerLink]="['/foreign-national', fn.businessCode, 'dashboard']" [innerHTML]="fn.fullName | highlight : searchedKeyword" [attr.aria-label]="fn.fullName">{{
                      fn.fullName
                    }}</a>
                  </h3>
                  <div [innerHTML]="joinValues(fn.company, toPTDateString(fn.birthDate)) | highlight : searchedKeyword"></div>
                  <div [innerHTML]="joinValues(fn.mobile, fn.businessCode, fn.email) | highlight : searchedKeyword"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Matters -->
          <div *ngIf="searchResult?.matters?.length && (tab.value === -1 || tab.value === EntityType.Matter)" class="result-section">
            <div *ngFor="let matter of searchResult!.matters" class="result-item">
              <div class="flex align-content-start gap-3">
                <i class="pi pi-briefcase"></i>
                <div class="flex flex-column gap-2">
                  <h3>
                    <a [routerLink]="['/matter', matter.businessCode]" [innerHTML]="matter.matterName | highlight : searchedKeyword" [attr.aria-label]="matter.matterName">{{
                      matter.matterName
                    }}</a>
                  </h3>
                  <div [innerHTML]="joinValues(matter.fnFullName, matter.fnBusinessCode, matter.fnEmail) | highlight : searchedKeyword"></div>
                  <span [innerHTML]="joinValues(matter.primReceiptNumber, matter.businessCode) | highlight : searchedKeyword"></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts -->
          <div *ngIf="searchResult?.contacts?.length && (tab.value === -1 || tab.value === EntityType.CompanyContact)" class="result-section">
            <div *ngFor="let contact of searchResult!.contacts" class="result-item">
              <div class="flex align-content-start gap-3">
                <i class="pi pi-id-card"></i>
                <div class="flex flex-column gap-2">
                  <h3>
                    <a
                      [routerLink]="['/company', contact.companyBusinessCode, 'users', contact.id]"
                      [innerHTML]="contact.fullName | highlight : searchedKeyword"
                      [attr.aria-label]="contact.fullName"
                      >{{ contact.fullName }}</a
                    >
                  </h3>
                  <div [innerHTML]="joinValues(contact.company, contact.companyBusinessCode) | highlight : searchedKeyword"></div>
                  <div [innerHTML]="joinValues(contact.phone, contact.email) | highlight : searchedKeyword"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Locations -->
          <div *ngIf="searchResult?.locations?.length && (tab.value === -1 || tab.value === EntityType.CompanyLocation)" class="result-section">
            <div *ngFor="let location of searchResult!.locations" class="result-item">
              <div class="flex align-content-start gap-3">
                <i class="pi pi-map-marker"></i>
                <div class="flex flex-column gap-2">
                  <h3>
                    <a
                      [routerLink]="['/company', location.companyBusinessCode, 'locations']"
                      [innerHTML]="(location.locationTitle || joinValues(location.street, location.city, location.state)) | highlight : searchedKeyword"
                      [attr.aria-label]="location.locationTitle"
                      >{{ location.locationTitle || joinValues(location.street, location.city, location.state) }}</a
                    >
                  </h3>
                  <div *ngIf="location.locationTitle" [innerHTML]="joinValues(location.street, location.city, location.state) | highlight : searchedKeyword"></div>
                  <div [innerHTML]="joinValues(location.company, location.companyBusinessCode) | highlight : searchedKeyword"></div>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="!hasResults && !searchedKeyword" class="flex align-items-center justify-content-center no-results">Enter the keywords you want to search for.</div>
          <div *ngIf="!hasResults && searchedKeyword && !loading" class="flex no-results">No Results Found</div>
          <div *ngIf="!hasResults && searchedKeyword && loading" class="flex no-results">Searching for the matched results, please wait...</div>

          <!-- Pagination -->
          <div *ngIf="hasResults" class="mt-4">
            <p-paginator [rows]="pageSize" [totalRecords]="totalRecords" [first]="currentPage * pageSize" (onPageChange)="onPageChange($event)"> </p-paginator>
          </div>
        </div>
      </p-tabpanel>
    </p-tabpanels>
  </p-tabs>
</app-section-card>
<p-toast></p-toast>
